import { create } from "zustand";
import api from "../service/api";
import { toast } from "react-toastify";

export interface AgentFatalLog {
  id: number;
  firmId: number;
  level: string;
  message: string;
  details: any;
  createdAt: string;
}

interface EdgeAgentStore {
  loading: boolean;
  logs: AgentFatalLog[];
  integrations: any[];
  activeTemplates: any[];
  fetchLogs: (firmId: number) => Promise<void>;
  schemaDictionary: any[];
  fetchIntegrations: () => Promise<void>;
  fetchIntegrationTemplates: (appId: number) => Promise<void>;
  fetchSchemaDictionary: () => Promise<void>;
  regenerateKey: (firmId: number) => Promise<string | null>;
  updateConfig: (firmId: number, configData: { syncReadConfig: any; syncWriteConfig: any }) => Promise<any>;
  createIntegrationTemplate: (appId: number, name: string, targetEndpoint: string) => Promise<boolean>;
}

export const useEdgeAgentStore = create<EdgeAgentStore>((set) => ({
  loading: false,
  logs: [],
  integrations: [],
  activeTemplates: [],
  schemaDictionary: [],

  fetchLogs: async (firmId: number) => {
    set({ loading: true });
    try {
      const res = await api.get<AgentFatalLog[]>(`/firm/admin/${firmId}/edge-logs`);
      set({ logs: res.data, loading: false });
    } catch (err: any) {
      toast.error(err.response?.data?.message || "Loglar alınamadı");
      set({ loading: false });
    }
  },

  fetchIntegrations: async () => {
    set({ loading: true });
    try {
      const res = await api.get<any[]>(`/integration/application/admin/integrations`);
      set({ integrations: res.data, loading: false });
    } catch (err: any) {
      toast.error("Entegrasyon türleri alınamadı");
      set({ loading: false });
    }
  },

  fetchIntegrationTemplates: async (appId: number) => {
    try {
      const response = await api.get<any[]>(`/integration/application/admin/integrations/${appId}/templates`);
      set({ activeTemplates: response.data || [] });
    } catch (error: any) {
      toast.error('Entegrasyon şablonları alınamadı: ' + error.message);
    }
  },

  fetchSchemaDictionary: async () => {
    try {
      const response = await api.get<any[]>(`/integration/application/admin/integrations/schema-dictionary`);
      set({ schemaDictionary: response.data || [] });
    } catch (error: any) {
      toast.error('Şema sözlüğü alınamadı: ' + error.message);
    }
  },

  regenerateKey: async (firmId: number) => {
    set({ loading: true });
    try {
      const res = await api.post(`/firm/admin/${firmId}/edge-key/regenerate`);
      toast.success("Yeni gizli anahtar başarıyla üretildi!");
      set({ loading: false });
      return res.data.agentSecretKey;
    } catch (err: any) {
      toast.error(err.response?.data?.message || "Anahtar üretilemedi");
      set({ loading: false });
      return null;
    }
  },

  updateConfig: async (firmId: number, configData: { syncReadConfig: any; syncWriteConfig: any }) => {
    set({ loading: true });
    try {
      const res = await api.put(`/firm/admin/${firmId}/edge-config`, configData);
      toast.success("Konfigürasyon başarıyla güncellendi!");
      set({ loading: false });
      return res.data;
    } catch (err: any) {
      toast.error(err.response?.data?.message || "Konfigürasyon güncellenemedi");
      set({ loading: false });
      return null;
    }
  },

  createIntegrationTemplate: async (appId: number, name: string, targetEndpoint: string) => {
    set({ loading: true });
    try {
      await api.post(`/integration/application/admin/integrations/${appId}/templates`, {
        name,
        targetEndpoint
      });
      toast.success("Yeni modül şablonu eklendi!");
      set({ loading: false });
      // Reload templates after adding
      useEdgeAgentStore.getState().fetchIntegrationTemplates(appId);
      return true;
    } catch (err: any) {
      toast.error(err.response?.data?.message || "Modül eklenemedi");
      set({ loading: false });
      return false;
    }
  }
}));
